/**
 * One paint per frame, worn by its wall card and its opened window (Part 10,
 * Rule 2): the card previews paint their gadget with it, and the window shell
 * (`components/focus/frame-window-shell.tsx`) passes it to the solid window
 * as `--frame-paint` and `data-paint`. A gadget with nothing to show is plain
 * paper on both, like its generic card.
 */
import {
  flightFields,
  hasFlight,
} from "@/components/shapes/flight-frame/flight-content";
import {
  hasTrack,
  musicFields,
} from "@/components/shapes/music-frame/music-content";
import {
  hasWeather,
  weatherFields,
} from "@/components/shapes/weather-frame/weather-fields";
import type { FrameData } from "@/lib/frame-data";
import { previewSource } from "@/lib/frame-preview-source";

/** The ink a paint takes: the card's, a gadget's white, or the code card's. */
export type CardInk = "card" | "gadget" | "code";

export interface CardPaint {
  /** A CSS background value, always a token. */
  background: string;
  ink: CardInk;
}

const SURFACE: CardPaint = { background: "var(--card-surface)", ink: "card" };
/** Terminal and code: dark in every theme. */
const CODE: CardPaint = { background: "var(--gadget-code)", ink: "code" };
const FLIGHT: CardPaint = { background: "var(--gadget-flight)", ink: "gadget" };
const MUSIC: CardPaint = { background: "var(--gadget-music)", ink: "gadget" };

/** The sky a weather condition paints with (`--gadget-weather-*`). */
export function weatherSky(condition: string | null): string {
  const text = condition?.toLowerCase() ?? "";
  if (/thunder|storm|lightning/.test(text)) return "storm";
  if (/snow|sleet|ice|hail/.test(text)) return "snow";
  if (/rain|shower|drizzle/.test(text)) return "rain";
  if (/moon|night/.test(text)) return "night";
  if (/cloud|overcast|fog|mist|haze/.test(text)) return "cloud";
  return "clear";
}

/** The paint of `frame`'s card and window. */
export function cardPaint(frame: FrameData): CardPaint {
  switch (frame.frameType) {
    case "terminal":
    case "code":
      return CODE;
    case "flight":
      return hasFlight(flightFields(previewSource(frame))) ? FLIGHT : SURFACE;
    case "music":
      return hasTrack(musicFields(previewSource(frame))) ? MUSIC : SURFACE;
    case "weather": {
      const weather = weatherFields(previewSource(frame));
      if (!hasWeather(weather)) return SURFACE;
      return {
        background: `var(--gadget-weather-${weatherSky(weather.condition)})`,
        ink: "gadget",
      };
    }
    default:
      return SURFACE;
  }
}
